import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { CampaignProvider, useCampaign } from '../context/CampaignContext';
import { useDaggerheartData } from '../context/DataContext';
import { rtdb } from '../firebase/config';
import { ref, set, onValue, push, remove, serverTimestamp } from 'firebase/database';
import { collection, query, where, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import MakeFeastManager from './MakeFeastManager';
import Modal from './common/Modal';
import CookingPotAnimation from './animations/CookingPotAnimation';
import { generateRecipeWithAI, formatCreditsMessage, isAIGenerationAvailable } from '../services/aiRecipeService';
import { refreshTokens } from '../services/SimpleTokenService';
import VitalAdjuster from './VitalAdjuster';

/**
 * UnifiedBeastFeastManager Component
 * Automatically detects online party members and switches between solo and collaborative
 * Beast Feast modes. If party members are online, collaborative mode is used; otherwise,
 * it falls back to solo cooking.
 *
 * @param {object} props - The component props.
 * @param {object} props.character - The current character data object.
 * @param {string} props.campaignId - The campaign ID for multiplayer coordination.
 * @param {function} props.updateCharacter - Function to update the character state.
 * @param {function} props.onManagerClose - Callback to close the modal.
 */
const UnifiedBeastFeastManager = ({ character, campaignId, updateCharacter, onManagerClose }) => {
  if (!campaignId) {
    // No campaign, use solo mode
    return (
      <Modal show={true} onClose={onManagerClose} size="max">
        <MakeFeastManager
          character={character}
          updateCharacter={updateCharacter}
          onManagerClose={onManagerClose}
          campaignId={campaignId}
          isInCampaign={false}
        />
      </Modal>
    );
  }

  return (
    <CampaignProvider campaignId={campaignId}>
      <UnifiedBeastFeastContent 
        character={character} 
        campaignId={campaignId}
        updateCharacter={updateCharacter} 
        onManagerClose={onManagerClose} 
      />
    </CampaignProvider>
  );
};

const UnifiedBeastFeastContent = ({ character, campaignId, updateCharacter, onManagerClose }) => {
  const { user } = useAuth();
  const { participants } = useCampaign();
  const [collaborativeMode, setCollaborativeMode] = useState(false);
  const [isCheckingParty, setIsCheckingParty] = useState(true);

  // Check for online party members and decide mode
  useEffect(() => {
    if (!participants) {
      setIsCheckingParty(true);
      return;
    }

    // Count online participants excluding current user
    const otherParticipants = participants.filter(p => p.userId !== user?.uid);
    
    // If other party members are online, enable collaborative mode
    setCollaborativeMode(otherParticipants.length > 0);
    setIsCheckingParty(false);
    
  }, [participants, user]);

  // Show loading while checking for party members
  if (isCheckingParty) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
          <div className="text-center">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100">🍖 Preparing Beast Feast</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Checking for online party members...
            </p>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  // Route to appropriate mode
  if (collaborativeMode) {
    return (
      <CollaborativeBeastFeastManager 
        character={character}
        campaignId={campaignId}
        updateCharacter={updateCharacter}
        onManagerClose={onManagerClose}
        isAutoCollaborative={true}
      />
    );
  } else {
    // Use solo MakeFeastManager wrapped in a modal since collaborative version doesn't use Modal
    return (
      <Modal title="🍽️ Make a Feast (Solo Mode)" onClose={onManagerClose}>
        <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <p className="text-sm text-blue-700 dark:text-blue-300">
            <strong>Solo Mode:</strong> No other party members are currently online. You're cooking alone!
          </p>
        </div>
        <MakeFeastManager
          character={character}
          updateCharacter={updateCharacter}
          onManagerClose={onManagerClose}
          campaignId={campaignId}
          isInCampaign={!!campaignId}
        />
      </Modal>
    );
  }
};

/**
 * CollaborativeBeastFeastManager Component
 * Handles collaborative "Make a Feast" sessions where party members can join
 * and contribute ingredients in real-time, with shared dice rolling and results.
 *
 * @param {object} props - The component props.
 * @param {object} props.character - The current character data object.
 * @param {string} props.campaignId - The campaign ID for multiplayer coordination.
 * @param {function} props.updateCharacter - Function to update the character state.
 * @param {function} props.onManagerClose - Callback to close the modal.
 * @param {boolean} props.isAutoCollaborative - Whether this was automatically switched to collaborative mode.
 */
const CollaborativeBeastFeastManager = ({ character, campaignId, updateCharacter, onManagerClose, isAutoCollaborative = false }) => {
  if (!campaignId) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
          <div className="text-center">
            <h2 className="text-xl font-bold mb-4">Character Not In Campaign</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              This character is not part of a campaign, so collaborative cooking is not available.
            </p>
            <button
              onClick={onManagerClose}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <CampaignProvider campaignId={campaignId}>
      <CollaborativeBeastFeastContent 
        character={character} 
        campaignId={campaignId}
        updateCharacter={updateCharacter} 
        onManagerClose={onManagerClose}
        isAutoCollaborative={isAutoCollaborative}
      />
    </CampaignProvider>
  );
};

const CollaborativeBeastFeastContent = ({ character, campaignId, updateCharacter, onManagerClose, isAutoCollaborative = false }) => {
  const { user } = useAuth();
  const { participants } = useCampaign();
  const allData = useDaggerheartData();

  // Cleanup tracking refs to prevent memory leaks
  const listenersRef = useRef(new Set());
  const isMountedRef = useRef(true);

  // Session state
  const [sessionId, setSessionId] = useState(null);
  const [isHost, setIsHost] = useState(false);
  const [feastSession, setFeastSession] = useState(null);
  const [joinableSession, setJoinableSession] = useState(null);
  
  // Cooking state
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [allIngredients, setAllIngredients] = useState([]); // Local cache of all contributed ingredients (like solo mode)
  const [dicePool, setDicePool] = useState([]);
  const [matchedSets, setMatchedSets] = useState([]);
  const [mealRating, setMealRating] = useState(0);
  const [cookingStep, setCookingStep] = useState('setup'); // setup, contributing, rolling, distributing, complete
  const [recipeTokens, setRecipeTokens] = useState(0);
  
  // Recipe state
  const [recipeName, setRecipeName] = useState('');
  const [recipeDescription, setRecipeDescription] = useState('');

  // AI generation state
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [aiCreditsRemaining, setAiCreditsRemaining] = useState(5); // Default to 5, will be updated

  // Distribution state (per player)
  const [distribution, setDistribution] = useState({
    hp: 0,
    stress: 0,
    hope: 0
  });

  // External animation control state
  const [isExternallyStirring, setIsExternallyStirring] = useState(false);
  const [externalCookingPhase, setExternalCookingPhase] = useState('prepare'); // Match solo mode initial state
  
  // Animation state for ingredient drops
  const [animationIngredients, setAnimationIngredients] = useState([]);
  const [animationResetTrigger, setAnimationResetTrigger] = useState(0);

  // Get available ingredients for this character
  const availableIngredients = character.inventory?.ingredients || [];
  
  // Get party cookbook (shared across all characters in campaign)
  const [partyCookbook, setPartyCookbook] = useState([]);
  
  // State for cookbook display
  const [showCookbook, setShowCookbook] = useState(false);

  // Early return if data is not loaded
  if (!allData || !allData.campaignFrames || !allData.campaignFrames['beast-feast']) {
    return <div>Loading Beast Feast data...</div>;
  }

    // Load party cookbook from campaign
  useEffect(() => {
    if (!campaignId) return;

    const loadPartyCookbook = async () => {
      try {
        // Load campaign cookbook directly from campaign document
        const campaignDoc = await getDoc(doc(db, 'campaigns', campaignId));
        if (!campaignDoc.exists()) return;
        
        const campaignData = campaignDoc.data();
        const campaignCookbook = campaignData.cookbook || [];
        
        setPartyCookbook(campaignCookbook);
      } catch (error) {
        console.error('Error loading party cookbook:', error);
      }
    };

    loadPartyCookbook();
  }, [campaignId]);

  // Listen for existing beast feast sessions
  useEffect(() => {
    if (!campaignId || !user) return;

    const sessionsRef = ref(rtdb, `campaigns/${campaignId}/beastFeastSessions`);
    const unsubscribe = onValue(sessionsRef, (snapshot) => {
      const sessions = snapshot.val();
      console.log('CollaborativeBeastFeastManager - All sessions:', sessions);
      
      if (sessions) {
        // Find active sessions that are not complete or cancelled
        const activeSessions = Object.entries(sessions).filter(([id, session]) => 
          session.status !== 'complete' && session.status !== 'cancelled'
        );
        
        console.log('CollaborativeBeastFeastManager - Active sessions:', activeSessions);
        
        // Check if user is already in an active session
        const userActiveSession = activeSessions.find(([id, session]) => 
          session.participants && session.participants[user.uid]
        );
        
        if (userActiveSession) {
          // User is already in a session - set it as current and clear joinable
          const [id, session] = userActiveSession;
          console.log('CollaborativeBeastFeastManager - User already in session:', id);
          
          // Only set sessionId if we don't already have one or it's different
          if (sessionId !== id) {
            setSessionId(id);
            setIsHost(session.hostId === user.uid);
          }
          setJoinableSession(null);
          
        } else if (!sessionId) {
          // User is not in any session, look for joinable sessions
          const joinableSessions = activeSessions.filter(([id, session]) => {
            // Can join if session is in setup phase and user is not already in it
            return session.cookingStep === 'setup' && 
                   (!session.participants || !session.participants[user.uid]);
          });
          
          if (joinableSessions.length > 0) {
            const [id, session] = joinableSessions[0]; // Take the first joinable session
            console.log('CollaborativeBeastFeastManager - Found joinable session:', { id, ...session });
            setJoinableSession({ id, ...session });
          } else {
            setJoinableSession(null);
          }
        } else {
          // User has a sessionId but no active session found - clear it
          setJoinableSession(null);
        }
      } else {
        setJoinableSession(null);
        // If no sessions exist and we think we're in one, clear it
        if (sessionId) {
          setSessionId(null);
          setIsHost(false);
          setFeastSession(null);
        }
      }
    });

    // Track listener for cleanup
    listenersRef.current.add(unsubscribe);

    return () => {
      listenersRef.current.delete(unsubscribe);
      unsubscribe();
    };
  }, [campaignId, user, sessionId]);

  // Listen to the current session if we're in one
  useEffect(() => {
    if (!sessionId || !campaignId) return;

    console.log('CollaborativeBeastFeastManager - Setting up session listener for:', sessionId);

    const sessionRef = ref(rtdb, `campaigns/${campaignId}/beastFeastSessions/${sessionId}`);
    const unsubscribe = onValue(sessionRef, (snapshot) => {
      const session = snapshot.val();
      console.log('CollaborativeBeastFeastManager - Session data received:', session);
      if (session) {
        setFeastSession(session);

        // FIX: Always update from Firebase - React handles deduplication
        // Don't compare against local state (causes stale closure issues in useEffect)
        // Don't check isMountedRef - React handles post-unmount updates safely

        // Always update these from Firebase (React will skip re-render if unchanged)
        if (session.dicePool) setDicePool(session.dicePool);
        if (session.matchedSets) setMatchedSets(session.matchedSets);
        if (session.mealRating !== undefined) setMealRating(session.mealRating);
        if (session.recipeTokens !== undefined) setRecipeTokens(session.recipeTokens);
        if (session.allIngredients !== undefined) setAllIngredients(session.allIngredients);

        // Update cookingStep (critical for UI state transitions)
        if (session.cookingStep) {
          console.log('CollaborativeBeastFeastManager - Updating cookingStep from', cookingStep, 'to', session.cookingStep);
          setCookingStep(session.cookingStep);
        }
      }
    });

    // Track listener for cleanup
    listenersRef.current.add(unsubscribe);

    return () => {
      listenersRef.current.delete(unsubscribe);
      unsubscribe();
    };
  }, [sessionId, campaignId]);

  // Control external animation based on cooking step
  useEffect(() => {
    if (cookingStep === 'rolling') {
      setIsExternallyStirring(true);
      setExternalCookingPhase('cooking');
    } else if (cookingStep === 'distributing') {
      setIsExternallyStirring(false);
      setExternalCookingPhase('complete');
    } else {
      setIsExternallyStirring(false);
      setExternalCookingPhase('prepare'); // Use 'prepare' instead of 'setup' to match solo mode
    }
  }, [cookingStep]);

  // Update animation ingredients when session participants change
  useEffect(() => {
    if (!feastSession?.participants) {
      setAnimationIngredients([]);
      return;
    }

    // Collect all contributed ingredients from all participants
    const allContributedIngredients = [];
    Object.values(feastSession.participants).forEach(participant => {
      if (participant.contributedIngredients) {
        allContributedIngredients.push(...participant.contributedIngredients);
      }
    });

    // Update animation ingredients to show all contributed ingredients
    setAnimationIngredients(allContributedIngredients);
  }, [feastSession?.participants]);

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      // Clean up all tracked listeners
      listenersRef.current.forEach(unsubscribe => {
        try {
          unsubscribe();
        } catch (error) {
          console.warn('Error cleaning up listener:', error);
        }
      });
      listenersRef.current.clear();
    };
  }, []);

  // Helper function to get flavor color classes
  const getFlavorColorClass = (flavor) => {
    const colorMap = {
      'Sweet': 'bg-pink-100 text-pink-800 border-pink-300',
      'Salty': 'bg-blue-100 text-blue-800 border-blue-300',
      'Bitter': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'Sour': 'bg-green-100 text-green-800 border-green-300',
      'Savory': 'bg-purple-100 text-purple-800 border-purple-300',
      'Weird': 'bg-red-100 text-red-800 border-red-300'
    };
    return colorMap[flavor] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  // Helper function to get die size for flavor
  const getFlavorDieSize = (flavor) => {
    const dieMap = {
      'Sweet': 4,
      'Salty': 6,
      'Bitter': 8,
      'Sour': 10,
      'Savory': 12,
      'Weird': 20
    };
    return dieMap[flavor] || 6;
  };

  // Create a new feast session
  const createFeastSession = async () => {
    if (!campaignId || !user) return;

    try {
      const sessionsRef = ref(rtdb, `campaigns/${campaignId}/beastFeastSessions`);
      const newSessionRef = push(sessionsRef);
      const newSessionId = newSessionRef.key;

      const sessionData = {
        hostId: user.uid,
        hostName: user.displayName || user.email || 'Unknown User',
        status: 'setup',
        participants: {
          [user.uid]: {
            displayName: user.displayName || user.email || 'Unknown User',
            characterId: character.id,
            characterName: character.name,
            contributedIngredients: [],
            isReady: false
          }
        },
        cookingStep: 'setup',
        dicePool: [],
        matchedSets: [],
        mealRating: 0,
        recipeTokens: 0,
        createdAt: serverTimestamp()
      };

      await set(newSessionRef, sessionData);
      setSessionId(newSessionId);
      setIsHost(true);
      setFeastSession(sessionData);
      setCookingStep('setup');
      setJoinableSession(null); // Clear any joinable session since we're creating our own
      
    } catch (error) {
      console.error('Error creating feast session:', error);
      toast.success('Failed to create feast session. Please try again.');
    }
  };

  // Join an existing feast session
  const joinFeastSession = async (joinSessionId) => {
    if (!campaignId || !user || !joinSessionId) return;

    try {
      // First check if the session still exists and is joinable
      const sessionSnapshot = await new Promise((resolve) => {
        const sessionRef = ref(rtdb, `campaigns/${campaignId}/beastFeastSessions/${joinSessionId}`);
        onValue(sessionRef, resolve, { onlyOnce: true });
      });
      
      const sessionData = sessionSnapshot.val();
      if (!sessionData) {
        toast.success('This session no longer exists.');
        return;
      }
      
      if (sessionData.status === 'complete' || sessionData.status === 'cancelled') {
        toast.success('This session is no longer available.');
        return;
      }
      
      if (sessionData.cookingStep !== 'setup') {
        toast.success('This session has already started cooking and cannot be joined.');
        return;
      }
      
      // Check if user is already in this session
      if (sessionData.participants && sessionData.participants[user.uid]) {
        console.log('User already in session, setting local state');
        setSessionId(joinSessionId);
        setIsHost(sessionData.hostId === user.uid);
        setJoinableSession(null);
        return;
      }

      const participantData = {
        displayName: user.displayName || user.email || 'Unknown User',
        characterId: character.id,
        characterName: character.name,
        contributedIngredients: [],
        isReady: false
      };

      // Add ourselves to the session
      const participantRef = ref(rtdb, `campaigns/${campaignId}/beastFeastSessions/${joinSessionId}/participants/${user.uid}`);
      await set(participantRef, participantData);

      setSessionId(joinSessionId);
      setIsHost(false);
      setJoinableSession(null);
      
    } catch (error) {
      console.error('Error joining feast session:', error);
      toast.success('Failed to join feast session. Please try again.');
    }
  };

  // Leave the current session
  const leaveFeastSession = async () => {
    if (!campaignId || !user) return;

    try {
      if (sessionId) {
        if (isHost) {
          // If host is leaving, cancel the session
          const sessionRef = ref(rtdb, `campaigns/${campaignId}/beastFeastSessions/${sessionId}`);
          await set(ref(rtdb, `campaigns/${campaignId}/beastFeastSessions/${sessionId}/status`), 'cancelled');
        } else {
          // Remove participant
          const participantRef = ref(rtdb, `campaigns/${campaignId}/beastFeastSessions/${sessionId}/participants/${user.uid}`);
          await remove(participantRef);
        }
      }

      // Atomic cleanup: Only remove session if host is leaving
      if (sessionId && isHost) {
        const sessionRef = ref(rtdb, `campaigns/${campaignId}/beastFeastSessions/${sessionId}`);
        await remove(sessionRef);
      }

      // Reset local state
      setSessionId(null);
      setIsHost(false);
      setFeastSession(null);
      setJoinableSession(null);
      setCookingStep('setup');
      setSelectedIngredients([]);
      setAllIngredients([]); // Reset ingredients cache
      setDicePool([]);
      setMatchedSets([]);
      setMealRating(0);

      // Reset animation state
      setAnimationIngredients([]);
      setAnimationResetTrigger(prev => prev + 1);
      
    } catch (error) {
      console.error('Error leaving feast session:', error);
    }
  };

  // Add ingredient to session
  const addIngredientToSession = async (ingredient) => {
    if (!sessionId || !campaignId || !user) return;

    try {
      // Add to our contributed ingredients
      const contributedRef = ref(rtdb, `campaigns/${campaignId}/beastFeastSessions/${sessionId}/participants/${user.uid}/contributedIngredients`);
      const newContributedIngredients = [...(feastSession?.participants?.[user.uid]?.contributedIngredients || []), ingredient];
      await set(contributedRef, newContributedIngredients);

      // Update selected ingredients locally
      setSelectedIngredients(prev => [...prev, ingredient]);

      // Remove from our inventory locally (will be saved when feast completes)
      const updatedInventory = { ...character.inventory };
      const ingredientIndex = updatedInventory.ingredients.findIndex(i => 
        i.name === ingredient.name && JSON.stringify(i.flavors) === JSON.stringify(ingredient.flavors)
      );
      if (ingredientIndex !== -1) {
        updatedInventory.ingredients.splice(ingredientIndex, 1);
        updateCharacter({ ...character, inventory: updatedInventory });
      }

    } catch (error) {
      console.error('Error adding ingredient to session:', error);
    }
  };

  // Mark ready to cook
  const markReady = async () => {
    if (!sessionId || !campaignId || !user) return;

    try {
      const readyRef = ref(rtdb, `campaigns/${campaignId}/beastFeastSessions/${sessionId}/participants/${user.uid}/isReady`);
      await set(readyRef, true);
    } catch (error) {
      console.error('Error marking ready:', error);
    }
  };

  // Start cooking (host only)
  const startCooking = async () => {
    if (!sessionId || !campaignId || !isHost || !feastSession) return;

    try {
      // Collect all contributed ingredients from all participants
      const allIngredients = [];
      Object.values(feastSession.participants || {}).forEach(participant => {
        if (participant.contributedIngredients) {
          allIngredients.push(...participant.contributedIngredients);
        }
      });

      // Calculate dice pool from all ingredients
      const { flavorProfiles } = allData.campaignFrames['beast-feast'].customMechanics.cooking;
      const flavorDice = {};
      allIngredients.forEach(ingredient => {
        (ingredient.flavors || []).forEach(flavor => {
          const profile = flavorProfiles.find(p => p.name === flavor.profile);
          if (profile) {
            const dieSize = parseInt(profile.die.slice(1)); // Remove 'd' from 'd4', 'd6', etc.
            const key = `d${dieSize}`;
            flavorDice[key] = (flavorDice[key] || 0) + flavor.strength;
          }
        });
      });

      // Convert to dice pool array
      const newDicePool = [];
      Object.entries(flavorDice).forEach(([dieSize, count]) => {
        for (let i = 0; i < count; i++) {
          newDicePool.push({
            id: `${dieSize}-${i}`,
            size: parseInt(dieSize.substring(1)),
            value: null,
            matched: false
          });
        }
      });

      // Check for recipe bonus tokens
      let bonusTokens = 0;
      const currentFlavors = Object.keys(flavorDice).sort().join(',');
      
      // Look for matching recipes in party cookbook
      const matchingRecipe = partyCookbook.find(recipe => {
        if (!recipe.flavorProfile) return false;
        const recipeFlavors = {};
        recipe.flavorProfile.forEach(flavor => {
          const profile = flavorProfiles.find(p => p.name === flavor.profile);
          if (profile) {
            const dieSize = parseInt(profile.die.slice(1));
            const key = `d${dieSize}`;
            recipeFlavors[key] = (recipeFlavors[key] || 0) + flavor.strength;
          }
        });
        const recipeFlavorString = Object.keys(recipeFlavors).sort().join(',');
        return recipeFlavorString === currentFlavors;
      });

      if (matchingRecipe) {
        // Add tokens equal to party tier (assume tier 1 for now, could be enhanced)
        bonusTokens = 1;
      }

      // Update session with cooking state using atomic transaction
      const sessionRef = ref(rtdb, `campaigns/${campaignId}/beastFeastSessions/${sessionId}`);

      // Use optimistic update pattern with rollback
      const optimisticState = {
        ...feastSession,
        cookingStep: 'rolling',
        dicePool: newDicePool,
        recipeTokens: bonusTokens,
        allIngredients
      };

      try {
        await set(sessionRef, optimisticState);
      } catch (error) {
        console.error('Failed to update session, rolling back:', error);
        // Rollback optimistic updates if Firebase write fails
        throw error;
      }

      // Start external animation control
      setIsExternallyStirring(true);
      setExternalCookingPhase('cooking');

    } catch (error) {
      console.error('Error starting cooking:', error);
    }
  };

  // Roll dice (host only)
  const rollDice = async () => {
    if (!sessionId || !campaignId || !isHost || !feastSession) return;

    try {
      // Roll all dice in the pool
      const rolledDice = (feastSession.dicePool || []).map(die => ({
        ...die,
        value: Math.floor(Math.random() * die.size) + 1,
        matched: false
      }));

      // Find matching values
      const valueGroups = {};
      rolledDice.forEach((die, index) => {
        const value = die.value;
        if (!valueGroups[value]) {
          valueGroups[value] = [];
        }
        valueGroups[value].push({ ...die, index });
      });

      // Identify matches (2 or more dice with same value)
      const newMatchedSets = [];
      const matchedIndices = new Set();
      
      Object.entries(valueGroups).forEach(([value, dice]) => {
        if (dice.length >= 2) {
          newMatchedSets.push({
            value: parseInt(value),
            dice: dice,
            totalValue: parseInt(value) // Each set contributes the matched value once
          });
          dice.forEach(die => matchedIndices.add(die.index));
        }
      });

      // Mark matched dice
      rolledDice.forEach((die, index) => {
        if (matchedIndices.has(index)) {
          die.matched = true;
        }
      });

      // Update session with accumulated matched sets
      const sessionRef = ref(rtdb, `campaigns/${campaignId}/beastFeastSessions/${sessionId}`);
      await set(sessionRef, {
        ...feastSession,
        dicePool: rolledDice,
        matchedSets: [...(feastSession.matchedSets || []), ...newMatchedSets]
      });

    } catch (error) {
      console.error('Error rolling dice:', error);
    }
  };

  // Continue cooking (remove one die and roll again)
  const continueCooking = async () => {
    if (!sessionId || !campaignId || !isHost || !feastSession) return;

    try {
      // Remove one unmatched die or use a token
      let newDicePool = [...(feastSession.dicePool || [])];
      let newRecipeTokens = feastSession.recipeTokens || 0;

      // First try to use a token
      if (newRecipeTokens > 0) {
        newRecipeTokens -= 1;
      } else {
        // Remove one unmatched die
        const unmatchedIndex = newDicePool.findIndex(die => !die.matched);
        if (unmatchedIndex !== -1) {
          newDicePool.splice(unmatchedIndex, 1);
        }
      }

      // If only one die left, cooking is done
      const unmatchedDice = newDicePool.filter(die => !die.matched);
      if (unmatchedDice.length <= 1) {
        // Calculate meal rating
        const totalMealRating = (feastSession.matchedSets || []).reduce((sum, set) => sum + set.totalValue, 0);
        
        // Update session to distributing phase
        const sessionRef = ref(rtdb, `campaigns/${campaignId}/beastFeastSessions/${sessionId}`);
        await set(sessionRef, {
          ...feastSession,
          cookingStep: 'distributing',
          dicePool: newDicePool,
          mealRating: totalMealRating,
          recipeTokens: newRecipeTokens
        });
      } else {
        // Continue cooking, reset only unmatched dice values
        const resetDice = newDicePool.map(die => ({
          ...die,
          value: die.matched ? die.value : null // Keep matched dice values, reset unmatched
          // Don't reset matched: false here - preserve matched status
        }));

        const sessionRef = ref(rtdb, `campaigns/${campaignId}/beastFeastSessions/${sessionId}`);
        await set(sessionRef, {
          ...feastSession,
          dicePool: resetDice,
          // Don't clear matchedSets - keep accumulating them
          recipeTokens: newRecipeTokens
        });
      }

    } catch (error) {
      console.error('Error continuing cooking:', error);
    }
  };

  // Generate recipe with AI
  const handleAIGeneration = async () => {
    if (isGeneratingAI) return;

    // Defensive check: ensure ingredients are available (use local state like solo mode)
    if (!allIngredients || allIngredients.length === 0) {
      toast.error('No ingredients available. Please cook the feast first before generating a recipe.');
      return;
    }

    setIsGeneratingAI(true);

    try {
      // Generate recipe with AI (use local state like solo mode)
      const result = await generateRecipeWithAI({
        ingredients: allIngredients,
        mealRating,
        campaignId: campaignId,
        userId: user?.uid || 'unknown'
      });

      if (result.success) {
        setRecipeName(result.name);
        setRecipeDescription(result.description);
        setAiCreditsRemaining(result.creditsRemaining);

        // Show cache notification if recipe was cached
        if (result.cached) {
          toast.success('Recipe retrieved from your cookbook history!', { duration: 3000 });
        }
      } else {
        // Update credits if available
        if (result.creditsRemaining !== null && result.creditsRemaining !== undefined) {
          setAiCreditsRemaining(result.creditsRemaining);
        }
        toast.error(result.error || 'Recipe generation failed. Try entering details manually.', { duration: 5000 });
      }
    } catch (error) {
      console.error('AI recipe generation error:', error);
      toast.error('Recipe generation failed unexpectedly. Try entering details manually.', { duration: 5000 });
    } finally {
      setIsGeneratingAI(false);
    }
  };

  // Distribute meal benefits
  const distributeMealBenefits = async () => {
    if (!sessionId || !campaignId || !user || !feastSession) return;

    try {
      const totalDistributed = distribution.hp + distribution.stress + distribution.hope;
      if (totalDistributed > feastSession.mealRating) {
        toast.success(`You can only distribute ${feastSession.mealRating} total points.`);
        return;
      }

      // Apply benefits to character
      const updatedCharacter = { ...character };
      
      // Heal HP
      if (distribution.hp > 0) {
        updatedCharacter.hp = Math.min(updatedCharacter.maxHp, updatedCharacter.hp + distribution.hp);
      }
      
      // Clear Stress
      if (distribution.stress > 0) {
        updatedCharacter.stress = Math.max(0, updatedCharacter.stress - distribution.stress);
      }
      
      // Gain Hope
      if (distribution.hope > 0) {
        updatedCharacter.hope = Math.min(updatedCharacter.maxHope, updatedCharacter.hope + distribution.hope);
      }

      // Save character changes
      await updateCharacter(updatedCharacter);

      // Mark as distributed in session
      const distributedRef = ref(rtdb, `campaigns/${campaignId}/beastFeastSessions/${sessionId}/participants/${user.uid}/distributed`);
      await set(distributedRef, {
        hp: distribution.hp,
        stress: distribution.stress,
        hope: distribution.hope,
        timestamp: serverTimestamp()
      });

    } catch (error) {
      console.error('Error distributing meal benefits:', error);
      toast.success('Failed to apply meal benefits. Please try again.');
    }
  };

  // Helper function to calculate flavor profile from ingredients
  const calculateFlavorProfile = (ingredients) => {
    const flavorTotals = {};
    ingredients.forEach(ingredient => {
      (ingredient.flavors || []).forEach(flavor => {
        if (!flavorTotals[flavor.profile]) {
          flavorTotals[flavor.profile] = 0;
        }
        flavorTotals[flavor.profile] += flavor.strength;
      });
    });

    return Object.entries(flavorTotals).map(([name, strength]) => ({
      profile: name,
      strength
    }));
  };

  // Complete the feast session
  const completeFeastSession = async () => {
    if (!sessionId || !campaignId || !isHost) return;

    try {
      // Apply host's benefits first (like solo mode)
      const totalDistributed = distribution.hp + distribution.stress + distribution.hope;
      if (totalDistributed > mealRating) {
        toast.error(`Cannot distribute more than ${mealRating} total points.`);
        return;
      }

      // Apply benefits to host's character
      const updatedCharacter = { ...character };

      if (distribution.hp > 0) {
        updatedCharacter.hp = Math.min(updatedCharacter.maxHp, updatedCharacter.hp + distribution.hp);
      }

      if (distribution.stress > 0) {
        updatedCharacter.stress = Math.max(0, updatedCharacter.stress - distribution.stress);
      }

      if (distribution.hope > 0) {
        updatedCharacter.hope = Math.min(updatedCharacter.maxHope, updatedCharacter.hope + distribution.hope);
      }

      // Refresh rest-based abilities (Beast Feast counts as a long rest)
      const spellcastTrait = (character.subclass && allData?.subclasses?.[character.subclass]?.spellcastTrait) || null;
      const refreshedCharacter = refreshTokens(updatedCharacter, 'long-rest', spellcastTrait);
      await updateCharacter(refreshedCharacter);

      // Save recipe to party cookbook if host provided details (use local state like solo mode)
      if (recipeName && recipeDescription && allIngredients && allIngredients.length > 0) {
        try {
          const recipe = {
            name: recipeName,
            description: recipeDescription,
            ingredients: allIngredients,
            flavorProfile: calculateFlavorProfile(allIngredients),
            mealRating: mealRating,
            createdAt: new Date().toISOString(),
            createdBy: user.displayName || user.email || 'Unknown User'
          };

          // Save recipe to campaign cookbook
          const campaignDoc = await getDoc(doc(db, 'campaigns', campaignId));
          if (campaignDoc.exists()) {
            const campaignData = campaignDoc.data();
            const existingCookbook = campaignData.cookbook || [];

            // Check if recipe already exists (by name and ingredients)
            const recipeExists = existingCookbook.some(existingRecipe =>
              existingRecipe.name === recipe.name &&
              JSON.stringify(existingRecipe.ingredients?.map(i => i.name).sort()) ===
              JSON.stringify(recipe.ingredients?.map(i => i.name).sort())
            );

            if (!recipeExists) {
              await updateDoc(doc(db, 'campaigns', campaignId), {
                cookbook: [...existingCookbook, recipe]
              });

              // Update local party cookbook state
              setPartyCookbook(prev => [...prev, recipe]);

              // Success notification
              toast.success(`Recipe "${recipeName}" saved to party cookbook!`);
            } else {
              // Recipe already exists
              toast('Recipe already exists in party cookbook', { icon: 'ℹ️' });
            }
          } else {
            toast.error('Campaign not found. Recipe could not be saved.');
          }
        } catch (recipeError) {
          console.error('Error saving recipe to party cookbook:', recipeError);
          toast.error('Recipe could not be saved to party cookbook. Check permissions.', { duration: 5000 });
          // Don't throw - allow feast to complete even if recipe save fails
        }
      } else if (!recipeName || !recipeDescription) {
        // Recipe fields incomplete - warn but don't block completion
        toast('Recipe not saved: Add a name and description to save to cookbook', {
          icon: '⚠️',
          duration: 4000
        });
      }

      // Mark session as complete
      const sessionRef = ref(rtdb, `campaigns/${campaignId}/beastFeastSessions/${sessionId}`);
      await set(ref(rtdb, `campaigns/${campaignId}/beastFeastSessions/${sessionId}/status`), 'complete');

      // Clean up session immediately
      await remove(sessionRef);

      // Success notification
      toast.success('🔄 Rest abilities refreshed! Feast complete.', { duration: 3000 });

      // Reset local state and close modal
      setSessionId(null);
      setIsHost(false);
      setFeastSession(null);
      setJoinableSession(null);
      setCookingStep('setup');
      setSelectedIngredients([]);
      setAllIngredients([]); // Reset ingredients cache
      setDicePool([]);
      setMatchedSets([]);
      setMealRating(0);
      setRecipeName('');
      setRecipeDescription('');

      // Stop external animation and reset
      setIsExternallyStirring(false);
      setExternalCookingPhase('prepare');
      setAnimationIngredients([]);
      setAnimationResetTrigger(prev => prev + 1);
      
      // Close the collaborative feast manager
      onManagerClose();

    } catch (error) {
      console.error('Error completing feast session:', error);
      toast.error('Failed to complete feast session. Please try again.');
    }
  };

  // Render the component based on current state
  if (!sessionId && !joinableSession) {
    // Initial state - can create new session or wait for others
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              🍖 Collaborative Beast Feast
            </h2>
            <button
              onClick={onManagerClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              ✕
            </button>
          </div>

          <div className="space-y-6">
            {/* Cooking Animation - Always visible */}
            <div className="flex justify-center">
              <CookingPotAnimation
                ingredients={animationIngredients}
                resetTrigger={animationResetTrigger}
                isExternallyStirring={isExternallyStirring}
                externalCookingPhase={externalCookingPhase}
                onCookingComplete={() => {
                  console.log('Collaborative cooking animation completed!');
                }}
              />
            </div>

            {isAutoCollaborative && (
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                <h3 className="font-semibold text-green-800 dark:text-green-200 mb-2">
                  🎉 Collaborative Mode Activated!
                </h3>
                <p className="text-green-700 dark:text-green-300 text-sm">
                  Other party members are online, so collaborative cooking is automatically enabled!
                </p>
              </div>
            )}
            
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
              <h3 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">
                Party Cooking Session
              </h3>
              <p className="text-blue-700 dark:text-blue-300 text-sm">
                Create a collaborative cooking session where party members can contribute ingredients 
                and watch the cooking process together in real-time!
              </p>
            </div>

            {participants.length > 1 ? (
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">Online Party Members:</h4>
                  <div className="space-y-2">
                    {participants.filter(p => p.userId !== user.uid).map(participant => (
                      <div key={participant.userId} className="flex items-center space-x-2 p-2 bg-gray-50 dark:bg-gray-700 rounded">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span>{participant.displayName}</span>
                        <span className="text-sm text-gray-500">Online</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={createFeastSession}
                    className="w-full px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors"
                  >
                    🍳 Start New Cooking Session
                  </button>
                  
                  <p className="text-center text-sm text-gray-600 dark:text-gray-400">
                    or wait for another party member to start a session
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center p-8 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  No other party members are currently online.
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-500">
                  You can still use the regular Make a Feast option for solo cooking.
                </p>
                <button
                  onClick={onManagerClose}
                  className="mt-4 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            )}

            {/* Party Cookbook */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <h4 className="font-semibold">Party Cookbook</h4>
                <button
                  onClick={() => setShowCookbook(!showCookbook)}
                  className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-sm"
                >
                  📖 {showCookbook ? 'Hide' : 'View'} Recipes
                </button>
              </div>
              
              {showCookbook && (
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                  {partyCookbook.length === 0 ? (
                    <p className="text-blue-700 dark:text-blue-300 text-sm">No recipes recorded yet. Complete your first collaborative feast to start your party cookbook!</p>
                  ) : (
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {partyCookbook.map((recipe, index) => (
                        <div key={index} className="bg-white dark:bg-gray-700 p-3 rounded-lg border">
                          <div className="flex justify-between items-start mb-2">
                            <h5 className="font-medium text-gray-900 dark:text-gray-100">{recipe.name}</h5>
                            <span className="text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-1 rounded">
                              Rating: {recipe.mealRating}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{recipe.description}</p>
                          
                          {/* Show flavor profile */}
                          {recipe.flavorProfile && recipe.flavorProfile.length > 0 && (
                            <div className="mb-2">
                              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Flavor Profile:</p>
                              <div className="flex flex-wrap gap-1">
                                {recipe.flavorProfile.map((flavor, idx) => (
                                  <span
                                    key={idx}
                                    className={`px-2 py-1 text-xs rounded border ${getFlavorColorClass(flavor.profile)}`}
                                  >
                                    {flavor.profile} ({flavor.strength})
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Show original ingredients */}
                          {recipe.ingredients && recipe.ingredients.length > 0 && (
                            <div className="mb-2">
                              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Ingredients used:</p>
                              <div className="flex flex-wrap gap-1">
                                {recipe.ingredients.map((ing, idx) => (
                                  <span key={idx} className="text-xs bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 px-2 py-1 rounded">
                                    {ing.name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Created by {recipe.createdBy} on {new Date(recipe.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {availableIngredients.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2">Your Available Ingredients:</h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {availableIngredients.map((ingredient, index) => (
                    <div key={index} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium">{ingredient.name}</h4>
                          {ingredient.description && (
                            <p className="text-sm text-gray-600 dark:text-gray-400">{ingredient.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {(ingredient.flavors || []).map((flavor, flavorIndex) => (
                          <span
                            key={flavorIndex}
                            className={`px-2 py-1 text-xs rounded border ${getFlavorColorClass(flavor.profile)}`}
                          >
                            {flavor.profile} ({flavor.strength})
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (joinableSession && !sessionId) {
    // Show joinable session
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              🍖 Join Beast Feast Session
            </h2>
            <button
              onClick={onManagerClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              ✕
            </button>
          </div>

          <div className="space-y-6">
            {/* Cooking Animation - Always visible */}
            <div className="flex justify-center">
              <CookingPotAnimation
                ingredients={animationIngredients}
                resetTrigger={animationResetTrigger}
                isExternallyStirring={isExternallyStirring}
                externalCookingPhase={externalCookingPhase}
                onCookingComplete={() => {
                  console.log('Collaborative cooking animation completed!');
                }}
              />
            </div>

            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
              <h3 className="font-semibold text-green-800 dark:text-green-200 mb-2">
                Active Cooking Session
              </h3>
              <p className="text-green-700 dark:text-green-300">
                <strong>{joinableSession.hostName}</strong> has started a collaborative cooking session.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">Current Participants:</h4>
              <div className="space-y-2">
                {Object.values(joinableSession.participants || {}).map(participant => (
                  <div key={participant.characterId} className="flex items-center space-x-2 p-2 bg-gray-50 dark:bg-gray-700 rounded">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span>{participant.characterName}</span>
                    <span className="text-sm text-gray-500">({participant.displayName})</span>
                    {participant.isReady && (
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Ready</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => joinFeastSession(joinableSession.id)}
                className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors"
              >
                🤝 Join Session
              </button>
              <button
                onClick={createFeastSession}
                className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
              >
                🍳 Start New Session
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (sessionId && feastSession) {
    // Show active session interface
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              🍖 Collaborative Beast Feast Session
            </h2>
            <div className="flex space-x-2">
              <button
                onClick={leaveFeastSession}
                className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm transition-colors"
              >
                Leave
              </button>
              <button
                onClick={onManagerClose}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Session Status */}
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-semibold text-blue-800 dark:text-blue-200">
                  Session hosted by {feastSession.hostName}
                </h3>
                <p className="text-blue-700 dark:text-blue-300 text-sm">
                  Phase: {cookingStep === 'setup' ? 'Contributing Ingredients' : 
                          cookingStep === 'rolling' ? 'Rolling Dice' : 
                          cookingStep === 'distributing' ? 'Distributing Benefits' : cookingStep}
                </p>
              </div>
              {isHost && (
                <div className="text-sm text-blue-600 dark:text-blue-400">
                  You are the host
                </div>
              )}
            </div>
          </div>

          {/* Participants */}
          <div className="mb-6">
            <h4 className="font-semibold mb-3">Participants:</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.entries(feastSession.participants || {}).map(([userId, participant]) => (
                <div key={userId} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium">{participant.characterName}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">{participant.displayName}</div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {participant.contributedIngredients?.length > 0 && (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                          {participant.contributedIngredients.length} ingredients
                        </span>
                      )}
                      {participant.isReady && (
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Ready</span>
                      )}
                      {participant.distributed && (
                        <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">Applied</span>
                      )}
                    </div>
                  </div>
                  {participant.contributedIngredients?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {participant.contributedIngredients.map((ingredient, idx) => (
                        <div key={idx} className="text-xs text-gray-600 dark:text-gray-400">
                          • {ingredient.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Cooking Animation - Always visible */}
          <div className="mb-6 flex justify-center">
            <CookingPotAnimation
              ingredients={animationIngredients}
              resetTrigger={animationResetTrigger}
              isExternallyStirring={isExternallyStirring}
              externalCookingPhase={externalCookingPhase}
              onCookingComplete={() => {
                console.log('Collaborative cooking animation completed!');
              }}
            />
          </div>

          {/* Phase-specific content */}
          {cookingStep === 'setup' && (
            <div className="space-y-6">
              {/* Add ingredients */}
              {availableIngredients.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3">Add Your Ingredients:</h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {availableIngredients.map((ingredient, index) => (
                      <div key={index} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium">{ingredient.name}</h4>
                                {ingredient.isSpecial && (
                                  <span className="text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 px-2 py-1 rounded font-medium">
                                    ⭐ Special
                                  </span>
                                )}
                              </div>
                              {ingredient.source && (
                                <span className="text-xs text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded">
                                  {ingredient.source}
                                </span>
                              )}
                            </div>
                            {ingredient.description && (
                              <p className="text-sm text-gray-600 dark:text-gray-400">{ingredient.description}</p>
                            )}
                            <div className="flex flex-wrap gap-1 mt-2">
                              {(ingredient.flavors || []).map((flavor, flavorIndex) => (
                                <span
                                  key={flavorIndex}
                                  className={`px-2 py-1 text-xs rounded border ${getFlavorColorClass(flavor.profile)}`}
                                >
                                  {flavor.profile} ({flavor.strength})
                                </span>
                              ))}
                            </div>
                            {ingredient.isSpecial && ingredient.specialFeature && (
                              <div className="text-xs text-yellow-700 dark:text-yellow-300 mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                                <strong>{ingredient.specialFeature.name}:</strong> {ingredient.specialFeature.description}
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => addIngredientToSession(ingredient)}
                            className="ml-3 px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm transition-colors"
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Ready button */}
              <div className="flex justify-center">
                <button
                  onClick={markReady}
                  disabled={feastSession?.participants?.[user.uid]?.isReady}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-semibold transition-colors"
                >
                  {feastSession?.participants?.[user.uid]?.isReady ? 'Ready!' : 'Mark Ready to Cook'}
                </button>
              </div>

              {/* Start cooking (host only) */}
              {isHost && (
                <div className="text-center">
                  <button
                    onClick={startCooking}
                    disabled={!Object.values(feastSession.participants || {}).every(p => p.isReady)}
                    className="px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg font-semibold transition-colors"
                  >
                    🍳 Start Cooking!
                  </button>
                  {!Object.values(feastSession.participants || {}).every(p => p.isReady) && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                      Waiting for all participants to be ready...
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {cookingStep === 'rolling' && (
            <div className="space-y-6">
              {/* Dice pool */}
              <div>
                <h4 className="font-semibold mb-3">Flavor Dice Pool:</h4>
                <div className="flex flex-wrap gap-2">
                  {feastSession.dicePool?.map((die, index) => (
                    <div
                      key={die.id || index}
                      className={`w-12 h-12 border-2 rounded-lg flex items-center justify-center font-bold ${
                        die.matched ? 'bg-green-100 border-green-500 text-green-800' : 
                        die.value ? 'bg-blue-100 border-blue-500 text-blue-800' : 
                        'bg-gray-100 border-gray-300 text-gray-800'
                      }`}
                    >
                      {die.value || `d${die.size}`}
                    </div>
                  ))}
                </div>
                {feastSession.recipeTokens > 0 && (
                  <p className="text-sm text-purple-600 dark:text-purple-400 mt-2">
                    🪙 Recipe Bonus Tokens: {feastSession.recipeTokens}
                  </p>
                )}
              </div>

              {/* Matched sets */}
              {feastSession.matchedSets?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3">Matched Sets:</h4>
                  <div className="space-y-2">
                    {(feastSession.matchedSets || []).map((set, index) => (
                      <div key={index} className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <span className="font-semibold text-green-800 dark:text-green-200">
                            {set.dice.length} dice showing {set.value}
                          </span>
                          <span className="text-sm text-green-600 dark:text-green-400">
                            (contributes {set.totalValue} to meal rating)
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Cooking actions (host only) */}
              {isHost && (
                <div className="flex justify-center space-x-4">
                  {!feastSession.dicePool?.some(die => die.value) ? (
                    <button
                      onClick={rollDice}
                      className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
                    >
                      🎲 Roll Dice
                    </button>
                  ) : (
                    <button
                      onClick={continueCooking}
                      className="px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-semibold transition-colors"
                    >
                      🍳 Continue Cooking
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {cookingStep === 'distributing' && (
            <div className="space-y-6">
              {/* Meal rating */}
              <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <h3 className="text-2xl font-bold text-green-800 dark:text-green-200">
                  🍽️ Meal Rating: {feastSession.mealRating}
                </h3>
                <p className="text-green-700 dark:text-green-300">
                  Distribute these points among HP healing, Stress clearing, and Hope gaining
                </p>
              </div>

              {/* Distribution controls */}
              <div className="space-y-4">
                <h4 className="font-semibold">Distribute Your Benefits:</h4>

                {/* Meal Rating Bar */}
                <div className="mb-6 p-4 bg-gray-900 rounded-lg border border-green-600">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-300">Meal Rating Available</span>
                    <span className="text-2xl font-bold text-green-400">
                      {feastSession.mealRating - (distribution.hp + distribution.stress + distribution.hope)} / {feastSession.mealRating}
                    </span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-3">
                    <div
                      className="bg-gradient-to-r from-green-500 to-green-600 h-3 rounded-full transition-all duration-300"
                      style={{
                        width: `${Math.max(0, Math.min(100, ((feastSession.mealRating - (distribution.hp + distribution.stress + distribution.hope)) / feastSession.mealRating) * 100))}%`
                      }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <VitalAdjuster
                    label="Heal HP"
                    vitalType="hp"
                    currentValue={character.hp || 0}
                    maxValue={character.maxHp || 8}
                    adjustmentValue={distribution.hp}
                    onAdjustmentChange={(delta) => {
                      const remainingBudget = feastSession.mealRating - (distribution.stress + distribution.hope);
                      const maxAllowed = Math.min(remainingBudget, (character.maxHp || 8) - (character.hp || 0));
                      const newValue = Math.max(0, Math.min(maxAllowed, distribution.hp + delta));
                      setDistribution(prev => ({ ...prev, hp: newValue }));
                    }}
                    remainingBudget={feastSession.mealRating - (distribution.stress + distribution.hope)}
                  />

                  <VitalAdjuster
                    label="Clear Stress"
                    vitalType="stress"
                    currentValue={character.stress || 0}
                    maxValue={character.maxStress || 5}
                    adjustmentValue={distribution.stress}
                    onAdjustmentChange={(delta) => {
                      const remainingBudget = feastSession.mealRating - (distribution.hp + distribution.hope);
                      const maxAllowed = Math.min(remainingBudget, character.stress || 0);
                      const newValue = Math.max(0, Math.min(maxAllowed, distribution.stress + delta));
                      setDistribution(prev => ({ ...prev, stress: newValue }));
                    }}
                    remainingBudget={feastSession.mealRating - (distribution.hp + distribution.hope)}
                  />

                  <VitalAdjuster
                    label="Gain Hope"
                    vitalType="hope"
                    currentValue={character.hope || 0}
                    maxValue={6}
                    adjustmentValue={distribution.hope}
                    onAdjustmentChange={(delta) => {
                      const remainingBudget = feastSession.mealRating - (distribution.hp + distribution.stress);
                      const maxAllowed = Math.min(remainingBudget, 6 - (character.hope || 0));
                      const newValue = Math.max(0, Math.min(maxAllowed, distribution.hope + delta));
                      setDistribution(prev => ({ ...prev, hope: newValue }));
                    }}
                    remainingBudget={feastSession.mealRating - (distribution.hp + distribution.stress)}
                  />
                </div>

                <div className="text-center p-3 bg-gray-900 rounded-lg border border-gray-600">
                  <p className="text-sm font-medium">
                    Total used: <span className={distribution.hp + distribution.stress + distribution.hope > feastSession.mealRating ? 'text-red-400' : 'text-green-400'}>
                      {distribution.hp + distribution.stress + distribution.hope}
                    </span> / {feastSession.mealRating}
                  </p>
                </div>
              </div>

              {/* Recipe naming (host only) */}
              {isHost && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100">Add to Party Cookbook:</h4>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <div className="flex gap-2 items-end mb-1">
                        <label className="block text-sm font-medium flex-1 text-gray-900 dark:text-gray-100">Recipe Name</label>
                        <button
                          onClick={handleAIGeneration}
                          disabled={!isAIGenerationAvailable(aiCreditsRemaining, isGeneratingAI)}
                          className="px-3 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-medium rounded-md hover:from-purple-600 hover:to-pink-600 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed transition-all flex items-center gap-1"
                          title="Discover a recipe"
                        >
                          {isGeneratingAI ? (
                            <>
                              <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Discovering...
                            </>
                          ) : (
                            <>
                              ✨ Discover Recipe
                            </>
                          )}
                        </button>
                      </div>
                      <input
                        type="text"
                        value={recipeName}
                        onChange={(e) => setRecipeName(e.target.value)}
                        placeholder="Give your creation a name..."
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-gray-100">Description</label>
                      <textarea
                        value={recipeDescription}
                        onChange={(e) => setRecipeDescription(e.target.value)}
                        placeholder="Describe how it turned out..."
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Complete feast (simplified like solo mode) */}
              <div className="flex justify-center">
                <button
                  onClick={isHost ? completeFeastSession : distributeMealBenefits}
                  disabled={distribution.hp + distribution.stress + distribution.hope > feastSession.mealRating}
                  className="px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg font-semibold transition-colors"
                >
                  {isHost ? 'Complete Feast Session' : 'Apply My Benefits'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default UnifiedBeastFeastManager;
